/*
 * MOUD — barre de cadeaux du panier, synchronisée avec l'offre Kaching.
 *
 * Paliers : 1) livraison 48h en point relais offerte dès un montant (réglage du thème),
 * puis un palier par pack Kaching qui débloque au moins un autre cadeau progressif.
 * Tout ce qui vient de Kaching (packs, quantités, cadeaux, titres, libellés) est lu
 * dans la config de l'app block : changer l'offre dans Kaching suffit.
 */
(function () {
  const DEAL_SELECTOR = 'script.kaching-bundles-deal-block-settings';
  const CACHE_KEY = 'moud-kaching-deal';
  const CACHE_TTL = 10 * 60 * 1000;

  // Dessins « trait fin » repris de snippets/moud-trait-icon.liquid (sans détails, petites tailles)
  const ICONS = {
    shipping: '<path d="M6.6 43.8Q6.2 31 7 19Q22 17.6 37.6 18.6L38 43.6M6.6 43.8L12.2 43.9M23.8 43.9L42.2 43.7M53.8 43.6L57.8 43.4L57.6 35.6Q54 30 49.6 25.8L38 25.6M41 28.6L48.6 28.7Q51.4 31.4 53 34.4L41.2 34.6Z"/><circle cx="18" cy="45.6" r="5.2"/><circle cx="48" cy="45.6" r="5.2"/>',
    home: '<path d="M32 12.6Q43 17 54.4 21.8L54 44Q43 49.6 32 54.4Q21 49.4 9.8 44.2L10 21.8Q21 17 32 12.6ZM10 21.8Q21 26.4 32 31Q43 26.4 54.4 21.8M32 31L32.2 54.2"/>',
    product: '<g transform="translate(0 -4)"><path d="M10 28Q32 26.6 54 28L53.6 36.4H10.4ZM13 36.4L13.4 55Q32 56.4 50.6 55L51 36.4M32 28V55.6M32 27.6Q22 15.6 17.4 20.2Q14 25 22 27.6M32 27.6Q42 15.6 46.6 20.2Q50 25 42 27.6"/></g>',
  };

  const money = (cents) =>
    new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(cents / 100);

  const esc = (s) =>
    String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);

  const packName = (q) => `Pack de ${q} sachet${q > 1 ? 's' : ''}`;

  function parseDeal(root) {
    const el = root.querySelector(DEAL_SELECTOR);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  // Config Kaching : sur la page si le widget y est, sinon dans la section produit de l'accueil.
  async function loadDeal(sectionId) {
    const onPage = parseDeal(document);
    if (onPage) {
      try {
        sessionStorage.setItem(CACHE_KEY, JSON.stringify({ t: Date.now(), deal: onPage }));
      } catch (e) {}
      return onPage;
    }
    try {
      const cached = JSON.parse(sessionStorage.getItem(CACHE_KEY));
      if (cached && Date.now() - cached.t < CACHE_TTL) return cached.deal;
    } catch (e) {}
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const html = await fetch(`${root}?section_id=${encodeURIComponent(sectionId)}`).then((r) => r.text());
    const deal = parseDeal(new DOMParser().parseFromString(html, 'text/html'));
    if (deal) {
      try {
        sessionStorage.setItem(CACHE_KEY, JSON.stringify({ t: Date.now(), deal }));
      } catch (e) {}
    }
    return deal;
  }

  function kachingProps(item) {
    try {
      return JSON.parse((item.properties || {})['__kaching_bundles'] || 'null');
    } catch (e) {
      return null;
    }
  }

  // Index (0-based) du pack Kaching présent dans le panier, -1 si aucun.
  function currentBarIndex(deal, cart) {
    const bars = deal.dealBars || [];
    let best = -1;
    let sachets = 0;
    cart.items.forEach((item) => {
      const p = kachingProps(item);
      if (!p || !p.main) return;
      sachets += item.quantity;
      const i = bars.findIndex((b) => b.id === p.bid);
      if (i > best) best = i;
    });
    if (best >= 0) return best;
    bars.forEach((b, i) => {
      if (sachets >= b.quantity) best = i;
    });
    return best;
  }

  function buildModel(deal, cart, opts) {
    const bars = deal.dealBars || [];
    const gifts = (deal.progressiveGiftsEnabled && deal.progressiveGifts && deal.progressiveGifts.gifts) || [];
    const barIndex = currentBarIndex(deal, cart);
    const isShipping = (g) => g.giftType === 'shipping';

    const giftTitle = (g) => {
      if (!/\{\{\s*product\s*\}\}/.test(g.title || '')) return g.title;
      const line = cart.items.find((it) => (kachingProps(it) || {}).gift === g.id);
      return line ? line.product_title : g.lockedTitle;
    };
    // Titre court du thème pour la barre (par id de cadeau Kaching), sinon celui de Kaching.
    const stepTitle = (g) => (opts.giftTitles && opts.giftTitles[g.id]) || giftTitle(g);

    // Palier 1 : point relais offert dès le seuil du thème. Une livraison offerte par Kaching
    // dès le 1er pack fait doublon : elle est fusionnée dans ce palier.
    const relayReached = cart.total_price >= opts.threshold;
    const steps = [
      {
        icon: 'shipping',
        reached: relayReached,
        caption: relayReached ? opts.relayCaption : `Dès ${money(opts.threshold)}`,
        title: opts.relayTitle,
      },
    ];

    // Puis un palier par pack qui débloque au moins un autre cadeau, dans l'ordre des packs.
    bars.forEach((bar, i) => {
      const atBar = gifts.filter((g) => g.unlockAtBar === i + 1 && !(i === 0 && isShipping(g)));
      if (!atBar.length) return;
      steps.push({
        icon: atBar.some(isShipping) ? 'home' : 'product',
        reached: barIndex >= i,
        bar,
        caption: barIndex >= i ? atBar[0].label : `${bar.quantity} sachet${bar.quantity > 1 ? 's' : ''}`,
        title: atBar.map(stepTitle).join(' + '),
        gifts: atBar,
      });
    });

    // Les livraisons offertes n'ont pas de ligne ✓ ici : la ligne « Frais de livraison »
    // au-dessus du total les affiche.
    const unlocked = [];
    steps.forEach((s) => {
      if (!s.reached || !s.gifts) return;
      s.gifts
        .filter((g) => !isShipping(g))
        .forEach((g) => unlocked.push(`${stepTitle(g)} ${String(g.label).toLowerCase()}`));
    });

    const next = steps.find((s) => !s.reached && s.bar);
    const nextBar = barIndex >= 0 ? bars[barIndex + 1] : null;
    let lastReached = -1;
    steps.forEach((s, i) => {
      if (s.reached) lastReached = i;
    });

    return {
      steps,
      unlocked,
      missing: relayReached ? null : opts.threshold - cart.total_price,
      next: next ? { pack: packName(next.bar.quantity), title: next.title } : null,
      upsell: nextBar ? { pack: packName(nextBar.quantity), barId: nextBar.id, quantity: nextBar.quantity } : null,
      fill: steps.length > 1 ? Math.max(lastReached, 0) / (steps.length - 1) : 1,
    };
  }

  const lcfirst = (t) => t.charAt(0).toLowerCase() + t.slice(1);

  const icon = (name) =>
    `<svg class="moud-gifts__icon moud-trait-icon" viewBox="0 0 64 64" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${ICONS[name]}</svg>`;

  function render(model, opts) {
    const lines = [];
    if (model.missing) {
      lines.push(`<p class="moud-gifts__next">Plus que <b>${money(model.missing)}</b> pour la ${esc(opts.relayUnlocked.toLowerCase())}</p>`);
    }
    model.unlocked.forEach((t) => lines.push(`<p class="moud-gifts__ok"><span aria-hidden="true">✓</span> ${esc(t)}</p>`));
    if (model.next) {
      lines.push(`<p class="moud-gifts__next">Passez au <b>${esc(model.next.pack)}</b> pour débloquer la <b>${esc(lcfirst(model.next.title))}</b>.</p>`);
    } else if (!model.missing) {
      lines.push(`<p class="moud-gifts__next">Tous vos cadeaux sont débloqués.</p>`);
    }

    const n = model.steps.length;
    const inset = 50 / n;
    const steps = model.steps
      .map(
        (s) => `<li class="moud-gifts__step${s.reached ? ' is-reached' : ''}">
          <span class="moud-gifts__dot">${icon(s.icon)}</span>
          <span class="moud-gifts__caption">${esc(s.caption)}</span>
          <span class="moud-gifts__title">${esc(s.title)}</span>
        </li>`
      )
      .join('');

    const upsell = model.upsell
      ? `<button type="button" class="moud-gifts__upsell" data-bar-id="${esc(model.upsell.barId)}" data-quantity="${model.upsell.quantity}"><span>Passer au ${esc(model.upsell.pack)}</span><span class="moud-gifts__upsell-price"></span></button>
         <p class="moud-gifts__upsell-note">${esc(opts.upsellNote.replace('{quantity}', model.upsell.quantity))}</p>`
      : '';

    return `<div class="moud-gifts">
      <div class="moud-gifts__text" role="status">${lines.join('')}</div>
      <div class="moud-gifts__track" style="--steps:${n}">
        <span class="moud-gifts__rail" style="left:${inset}%;right:${inset}%"><i style="width:${(model.fill * 100).toFixed(1)}%"></i></span>
        <ol class="moud-gifts__steps">${steps}</ol>
      </div>
      ${upsell}
    </div>`;
  }

  // --- Changement de pack (bouton d'upsell, + / −) -------------------------------------
  // Remplace le pack du panier (ligne principale + cadeaux) par le pack visé. En abonnement,
  // même fréquence de livraison si elle existe pour le pack visé, sinon la première proposée
  // (tous les mois). Variante, formule et remise lues dans Kaching et dans la fiche produit :
  // rien n'est codé en dur.

  const planFrequency = (plan) => {
    const opt = (plan.options || []).find((o) => o.name === 'config');
    try {
      return JSON.parse(opt.value).delivery.frequency;
    } catch (e) {
      return null;
    }
  };

  const randomId = () => Math.random().toString(36).slice(2, 6);

  // Kaching ne donne que l'ID produit du cadeau : la variante vient de l'API vitrine (sans jeton).
  async function firstVariantId(root, productGID) {
    const id = String(productGID).startsWith('gid://') ? productGID : `gid://shopify/Product/${productGID}`;
    const res = await fetch(`${root}api/2026-07/graphql.json`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        query: 'query($id: ID!) { product(id: $id) { variants(first: 10) { nodes { id availableForSale } } } }',
        variables: { id },
      }),
    }).then((r) => r.json());
    const nodes = (res.data && res.data.product && res.data.product.variants.nodes) || [];
    const v = nodes.find((n) => n.availableForSale) || nodes[0];
    return v ? v.id : null;
  }

  const cartRoot = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  // Lignes d'un pack Kaching : la ligne produit et ses cadeaux (même props.id).
  function packLines(cart, main) {
    const id = (kachingProps(main) || {}).id;
    return id ? cart.items.filter((it) => (kachingProps(it) || {}).id === id) : [main];
  }

  async function removeLines(lines) {
    const updates = {};
    lines.forEach((it) => (updates[it.key] = 0));
    await fetch(`${cartRoot()}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates }),
    });
  }

  // Remplace le pack du panier par le pack `barId`.
  // subscribe = true (bouton d'upsell) : toujours en abonnement.
  // subscribe = false (+ / −) : on garde le mode d'achat actuel (abonnement ou achat unique).
  const productCache = {};
  const loadProduct = (handle) =>
    (productCache[handle] = productCache[handle] || fetch(`${cartRoot()}products/${handle}.js`).then((r) => r.json()));

  // Pack visé : variante Kaching et formule (abonnement si subscribe ou si la ligne actuelle l'est).
  async function resolveTarget(deal, barId, cart, subscribe) {
    const bar = (deal.dealBars || []).find((b) => b.id === barId);
    const defaults = (deal.defaultVariantsV2 || []).find((d) => d.dealBarId === barId);
    if (!bar || !defaults) throw new Error('Pack Kaching introuvable : ' + barId);

    const main =
      cart.items.find((it) => (kachingProps(it) || {}).main) ||
      cart.items.find((it) => it.product_id === defaults.productGID);
    if (!main) throw new Error('Aucun pack MOUD dans le panier');

    const product = await loadProduct(main.handle);
    const variantId = defaults.variantGIDs[0];
    const variant = product.variants.find((v) => v.id === variantId);
    const plans = product.selling_plan_groups.flatMap((g) => g.selling_plans);
    const offered = variant.selling_plan_allocations
      .map((a) => plans.find((p) => p.id === a.selling_plan_id))
      .filter(Boolean);

    const currentPlan = main.selling_plan_allocation && main.selling_plan_allocation.selling_plan;
    let plan = null;
    if (subscribe || currentPlan) {
      if (!offered.length) throw new Error('Pas d’abonnement pour la variante ' + variantId);
      const currentFrequency = currentPlan ? planFrequency(plans.find((p) => p.id === currentPlan.id) || {}) : null;
      plan = offered.find((p) => planFrequency(p) === currentFrequency) || offered[0];
    }
    return { bar, defaults, main, variant, variantId, plan };
  }

  // Écart de prix entre le pack du panier (produit + cadeaux) et le pack visé en abonnement.
  async function upsellDelta(deal, barId, cart) {
    const { bar, main, variant, plan } = await resolveTarget(deal, barId, cart, true);
    const allocation = variant.selling_plan_allocations.find((a) => a.selling_plan_id === plan.id);
    const current = packLines(cart, main).reduce((sum, it) => sum + it.final_line_price, 0);
    return allocation.price * bar.quantity - current;
  }

  async function changePack(deal, barId, subscribe) {
    const root = cartRoot();
    const cart = await fetch(`${root}cart.js`).then((r) => r.json());
    const { bar, defaults, main, variantId, plan } = await resolveTarget(deal, barId, cart, subscribe);
    // Lignes à retirer : tout le pack Kaching (produit + cadeaux), ou à défaut le produit seul.
    const oldLines = (kachingProps(main) || {}).id
      ? packLines(cart, main)
      : cart.items.filter((it) => it.product_id === defaults.productGID);

    const props = {
      deal: deal.nanoId,
      pp: defaults.productGID,
      main: true,
      market: deal.marketId,
      v: deal.versionId,
      id: randomId(),
      bid: bar.id,
    };
    if (plan) {
      const adjustment = plan.price_adjustments[0] || {};
      props.sellingPlan = { id: plan.id, priceAdjustment: { type: adjustment.value_type, value: adjustment.value } };
    }

    // Cadeaux produit du pack visé : le widget Kaching les ajoute lui-même, pas un ajout
    // fait par script. On les ajoute avec les mêmes propriétés pour que Kaching les passe à 0 €.
    const barNumber = deal.dealBars.indexOf(bar) + 1;
    const giftItems = [];
    const gifts = (deal.progressiveGiftsEnabled && deal.progressiveGifts && deal.progressiveGifts.gifts) || [];
    for (const g of gifts) {
      if (g.giftType !== 'product' || !g.productGID) continue;
      if (g.unlockAtBarOnly ? g.unlockAtBar !== barNumber : g.unlockAtBar > barNumber) continue;
      if (g.applyOnlyForSubscriptions && !plan) continue;
      const giftVariant = (g.variantGIDs && g.variantGIDs[0]) || (await firstVariantId(root, g.productGID));
      if (!giftVariant) continue;
      giftItems.push({
        id: String(giftVariant).split('/').pop(),
        quantity: g.quantity || 1,
        properties: { __kaching_bundles: JSON.stringify({ id: props.id, deal: deal.nanoId, v: deal.versionId, gift: g.id }) },
      });
    }

    const mainItem = { id: variantId, quantity: bar.quantity, properties: { __kaching_bundles: JSON.stringify(props) } };
    if (plan) mainItem.selling_plan = plan.id;

    // Ajout d'abord : si ça échoue, l'ancien pack reste en place.
    const added = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [mainItem, ...giftItems] }),
    });
    if (!added.ok) throw new Error('Ajout refusé : ' + (await added.text()));
    if (oldLines.length) await removeLines(oldLines);
  }

  const upgradePack = (deal, barId) => changePack(deal, barId, true);

  window.MoudCartGifts = { loadDeal, buildModel, render, packName, upgradePack, changePack };

  // Lignes de pack : pas de quantité libre (sinon 3 ou 5 sachets à un prix incohérent).
  // + = pack suivant, − = pack précédent (ou retrait au plus petit), corbeille = pack + cadeaux.
  // Le champ quantité est en lecture seule ; le + est grisé sur le plus grand pack.
  function lockPacks(deal, cart) {
    const bars = deal.dealBars || [];
    cart.items.forEach((item) => {
      const p = kachingProps(item);
      if (!p || !p.main) return;
      let index = bars.findIndex((b) => b.id === p.bid);
      if (index < 0) index = bars.reduce((best, b, i) => (item.quantity >= b.quantity ? i : best), -1);

      document.querySelectorAll(`input[data-quantity-line-key="${CSS.escape(item.key)}"]`).forEach((input) => {
        const qty = input.closest('quantity-input');
        if (!qty || qty.dataset.moudLocked) return;
        qty.dataset.moudLocked = 'true';
        input.readOnly = true;
        input.tabIndex = -1;

        const plus = qty.querySelector('.quantity__button[name="plus"]');
        if (plus && index >= bars.length - 1) {
          plus.disabled = true;
          plus.title = 'Quantité maximale pour ce pack';
        }

        const run = async (action) => {
          qty.querySelectorAll('.quantity__button').forEach((b) => (b.disabled = true));
          qty.closest('.cart-item')?.querySelector('.loading__spinner')?.classList.remove('hidden');
          try {
            await action();
          } catch (e) {
            console.error('[MOUD] pack', e);
          }
          await refreshDrawer();
        };

        // Écoute en capture sur <quantity-input> : passe avant le script de Dawn sur les boutons.
        qty.addEventListener(
          'click',
          (event) => {
            const button = event.target.closest('.quantity__button');
            if (!button) return;
            event.preventDefault();
            event.stopImmediatePropagation();
            if (button.disabled) return;
            if (button.name === 'plus' && bars[index + 1]) {
              run(() => changePack(deal, bars[index + 1].id, false));
            } else if (button.name === 'minus') {
              run(() => (index > 0 ? changePack(deal, bars[index - 1].id, false) : removeLines(packLines(cart, item))));
            }
          },
          true
        );

        const remove = qty.closest('.cart-item')?.querySelector('cart-remove-button');
        if (remove) {
          remove.addEventListener(
            'click',
            (event) => {
              event.preventDefault();
              event.stopImmediatePropagation();
              run(() => removeLines(packLines(cart, item)));
            },
            true
          );
        }
      });
    });
  }

  // Recharge le tiroir panier et la pastille du header (mêmes sections que Dawn).
  // Sur la page /cart, on recharge la page.
  async function refreshDrawer() {
    if (document.querySelector('cart-items')) {
      window.location.reload();
      return;
    }
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    // En aperçu d'un thème non publié, le rendu de sections doit viser ce thème.
    const theme = window.Shopify && window.Shopify.theme;
    const preview = theme && theme.role !== 'main' ? `&preview_theme_id=${theme.id}` : '';
    const sections = await fetch(`${root}?sections=cart-drawer,cart-icon-bubble${preview}`).then((r) => r.json());
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.renderContents === 'function') {
      drawer.renderContents({ id: null, sections });
    } else {
      window.location.reload();
    }
  }

  // <moud-cart-gifts> : rendu dans le tiroir. Le tiroir est re-rendu à chaque
  // modification du panier, donc l'élément se reconstruit tout seul.
  class MoudCartGiftsElement extends HTMLElement {
    async connectedCallback() {
      let cart;
      try {
        cart = JSON.parse(this.querySelector('script[type="application/json"]').textContent);
      } catch (e) {
        return;
      }
      const opts = {
        threshold: Number(this.dataset.threshold),
        relayTitle: this.dataset.relayTitle,
        relayCaption: this.dataset.relayCaption,
        relayUnlocked: this.dataset.relayUnlocked,
        homeUnlocked: this.dataset.homeUnlocked,
        upsellNote: this.dataset.upsellNote,
        giftTitles: {},
      };
      try {
        opts.giftTitles = JSON.parse(this.dataset.giftTitles || '{}');
      } catch (e) {}
      let deal;
      try {
        deal = await loadDeal(this.dataset.dealSection);
      } catch (e) {
        deal = null;
      }
      if (!deal) return;

      this.querySelector('.moud-gifts-slot').innerHTML = render(buildModel(deal, cart, opts), opts);
      lockPacks(deal, cart);

      const button = this.querySelector('.moud-gifts__upsell');
      if (!button) return;
      upsellDelta(deal, button.dataset.barId, cart)
        .then((delta) => {
          if (delta > 0) button.querySelector('.moud-gifts__upsell-price').textContent = `+${money(delta)}`;
        })
        .catch((e) => console.error('[MOUD] upsellDelta', e));
      button.addEventListener('click', async () => {
        const label = button.innerHTML;
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        button.textContent = 'Mise à jour…';
        try {
          await upgradePack(deal, button.dataset.barId);
          await refreshDrawer();
        } catch (e) {
          console.error('[MOUD] upgradePack', e);
          button.disabled = false;
          button.removeAttribute('aria-busy');
          button.innerHTML = label;
        }
      });
    }
  }
  if (!customElements.get('moud-cart-gifts')) customElements.define('moud-cart-gifts', MoudCartGiftsElement);

  // <moud-cart-eta> : fourchette de livraison calculée dans le navigateur (heure de Paris),
  // avec les mêmes règles que le bloc « estimation de livraison » de la page produit.
  const DAY = 86400000;
  function parisNow() {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/Paris',
      year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
    }).formatToParts(new Date());
    const get = (t) => Number(parts.find((p) => p.type === t).value);
    return { date: Date.UTC(get('year'), get('month') - 1, get('day')), hhmm: get('hour') * 100 + get('minute') };
  }
  const weekday = (ts) => {
    const d = new Date(ts).getUTCDay();
    return d === 0 ? 7 : d;
  };
  const isOpen = (ts, weekend) => {
    const d = weekday(ts);
    if (d <= 5) return true;
    if (d === 6) return weekend !== 'none';
    return weekend === 'saturday_sunday';
  };

  class MoudCartEtaElement extends HTMLElement {
    connectedCallback() {
      const s = this.dataset;
      const now = parisNow();
      const cutoff = Number(String(s.cutoff || '00:00').replace(':', ''));

      let prep = Number(s.preparationDays || 0);
      let ship = now.date;
      for (let i = 0; i <= 30; i++) {
        const ts = now.date + i * DAY;
        let ok = isOpen(ts, s.preparationWeekend);
        if (i === 0 && now.hhmm >= cutoff) ok = false;
        if (!ok) continue;
        if (prep === 0) {
          ship = ts;
          break;
        }
        prep -= 1;
      }
      const addDeliveryDays = (from, n) => {
        let ts = from;
        while (n > 0) {
          ts += DAY;
          if (isOpen(ts, s.deliveryWeekend)) n -= 1;
        }
        return ts;
      };
      const min = addDeliveryDays(ship, Number(s.min || 2));
      const max = addDeliveryDays(ship, Math.max(Number(s.max || 4), Number(s.min || 2)));

      const fmt = (ts, opts) => new Intl.DateTimeFormat('fr-FR', { timeZone: 'UTC', ...opts }).format(new Date(ts));
      const sameMonth = new Date(min).getUTCMonth() === new Date(max).getUTCMonth();
      const from = fmt(min, sameMonth ? { weekday: 'short', day: 'numeric' } : { weekday: 'short', day: 'numeric', month: 'short' });
      const to = fmt(max, { weekday: 'short', day: 'numeric', month: 'short' });
      const out = this.querySelector('.moud-cart-eta__text');
      if (out && s.range === 'true') {
        out.innerHTML = `Livré entre le <b>${esc(from)}</b> et le <b>${esc(to)}</b>`;
      } else if (out) {
        out.innerHTML = `Livré le <b>${esc(fmt(min, { weekday: 'long', day: 'numeric', month: 'long' }))}</b>`;
      }
      this.hidden = false;
    }
  }
  if (!customElements.get('moud-cart-eta')) customElements.define('moud-cart-eta', MoudCartEtaElement);
})();
